/**
 * useSlideoutStack - Hook for managing a stack of slideouts.
 *
 * Thin wrapper around SlideoutStackContext. The context manages state and
 * URL sync; this hook provides the same public API consumers already use.
 */

import {
  type OpenSlideoutOptions,
  type PushSlideoutOptions,
  type SlideoutEntry,
  type SlideoutKind,
  type SlideoutPushInput,
  useSlideoutStackContext,
} from '../contexts/SlideoutStackContext';

export interface UseSlideoutStackResult {
  /** The current stack of entries (bottom to top) */
  stack: SlideoutEntry[];
  /** The top (currently visible) entry, or undefined if stack is empty */
  topEntry: SlideoutEntry | undefined;
  /**
   * The top object ID, or undefined if stack is empty OR the top entry is not
   * a CRM entity. Preserved for backward compatibility — new code should
   * prefer `topEntry`.
   */
  topId: string | undefined;
  /**
   * Push a slideout onto the stack (duplicates allowed for full back-navigation).
   *
   * Accepts a legacy string/number (treated as `entity` kind) or a typed
   * `SlideoutEntry`.
   */
  pushSlideout: (
    input: SlideoutPushInput,
    options?: PushSlideoutOptions
  ) => void;
  /**
   * App-level open: a click FROM THE APP replaces whatever slideouts are
   * layered above `options.base` (the whole stack when no base is given or
   * found); only a click from inside a slideout builds the stack via
   * `pushSlideout`. Guard-checked like `pushSlideout`.
   */
  openSlideout: (
    input: SlideoutPushInput,
    options?: OpenSlideoutOptions
  ) => void;
  /** Pop the top entry from the stack */
  popSlideout: () => void;
  /**
   * Replace the top entry without changing stack depth. Used for sequential
   * transitions (e.g. auto-advance to next proposal after approve).
   */
  replaceTop: (input: SlideoutPushInput) => void;
  /** Clear the entire stack */
  clearStack: () => void;
  /**
   * Close the slideout for `input` and everything layered above it. Finds
   * the LAST entry in the stack matching the input's kind+id and truncates
   * the stack at that index (entries below it — e.g. a `proposal` base on
   * the chat surface — stay). No-op when no entry matches. Routed through
   * the push guard exactly like `openSlideout`, so unsaved changes in the
   * currently-open slideout still intercept.
   */
  closeFromEntry: (input: SlideoutPushInput) => void;
  /**
   * Dismiss the record/thread layers while preserving a proposal underneath:
   * truncates the stack to (and INCLUDING) the TOPMOST `proposal` entry —
   * everything layered above that proposal is removed, and everything at or
   * below it (including any slideouts beneath the proposal) stays. So for a
   * `[entity, proposal, thread]` stack this keeps `[entity, proposal]`, not a
   * bare proposal run. Identical to `clearStack` when no proposal is on the
   * stack. Guard-routed like `openSlideout` — for callers with no local
   * unsaved-changes guard of their own.
   */
  clearToProposalBase: () => void;
  /**
   * Same truncation as `clearToProposalBase`, but unguarded — for hosts that
   * already resolved their own local unsaved-changes guard (via
   * `useGuardedDismiss`) before calling this as the `onClose` action.
   * Mirrors `popSlideout`.
   */
  dismissToProposalBase: () => void;
  /** Kinds claimed by an inline host — the global renderer skips these. */
  inlineHostKinds: ReadonlySet<SlideoutKind>;
  /** Claim inline hosting for the given kinds; returns an unregister fn. */
  registerInlineHost: (kinds: SlideoutKind[]) => () => void;
}

/**
 * Hook for managing a stack of slideouts.
 *
 * @example
 * ```tsx
 * const { stack, pushSlideout, popSlideout, topEntry } = useSlideoutStack();
 *
 * // Open an entity slideout (string/number defaults to entity kind)
 * pushSlideout(object.entity_id);
 *
 * // Open a proposal slideout
 * pushSlideout({ kind: 'proposal', id: String(proposalId) });
 *
 * // Close the top slideout
 * popSlideout();
 * ```
 */
export function useSlideoutStack(): UseSlideoutStackResult {
  const {
    stack,
    topEntry,
    topId,
    pushSlideout,
    openSlideout,
    popSlideout,
    replaceTop,
    clearStack,
    closeFromEntry,
    clearToProposalBase,
    dismissToProposalBase,
    inlineHostKinds,
    registerInlineHost,
  } = useSlideoutStackContext();

  return {
    stack,
    topEntry,
    topId,
    pushSlideout,
    openSlideout,
    popSlideout,
    replaceTop,
    clearStack,
    closeFromEntry,
    clearToProposalBase,
    dismissToProposalBase,
    inlineHostKinds,
    registerInlineHost,
  };
}
